import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { utils, write } from "xlsx";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { StatCard } from "@/components/admin/StatCard";
import { Pager } from "@/components/admin/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPaise } from "@/lib/format/money";
import { formatISTDateTime } from "@/lib/date/ist";
import { getRefundDestinationReport } from "@/lib/admin/order-refunds.functions";

import {
  getAdminWalletReport,
  getAdminWalletSettings,
  updateAdminWalletSettings,
  listAdminWalletTransactions,
  WALLET_TXN_TYPES,
  type AdminWalletSettings,
} from "@/lib/admin/wallet.functions";

export const Route = createFileRoute("/_admin/admin/wallet")({
  head: () => ({
    meta: [
      { title: "Wallet — Admin" },
      {
        name: "description",
        content: "Wallet liability, transactions, top users and settings.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: WalletPage,
});

const PAGE_SIZE = 25;

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function toCsv(rows: (string | number)[][]): string {
  return rows
    .map((r) =>
      r
        .map((cell) => {
          const s = String(cell ?? "");
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(","),
    )
    .join("\n");
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadXlsx(filename: string, rows: (string | number)[][]) {
  const ws = utils.aoa_to_sheet(rows);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, "Wallet");
  const buf = write(wb, { type: "array", bookType: "xlsx" }) as ArrayBuffer;
  const blob = new Blob([buf], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function WalletPage() {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(-1));

  const [txnSearchInput, setTxnSearchInput] = useState("");
  const [txnSearch, setTxnSearch] = useState("");
  const [txnType, setTxnType] = useState("all");
  const [txnFrom, setTxnFrom] = useState("");
  const [txnTo, setTxnTo] = useState("");
  const [txnPage, setTxnPage] = useState(1);

  const reportFn = useServerFn(getAdminWalletReport);
  const report = useQuery({
    queryKey: ["admin", "wallet-report", from, to],
    queryFn: () => reportFn({ data: { from, to } }),
    placeholderData: (prev) => prev,
  });

  const listTxnFn = useServerFn(listAdminWalletTransactions);
  const txns = useQuery({
    queryKey: ["admin", "wallet-txns", txnSearch, txnType, txnFrom, txnTo, txnPage],
    queryFn: () =>
      listTxnFn({
        data: {
          customerId: null,
          limit: PAGE_SIZE,
          offset: (txnPage - 1) * PAGE_SIZE,
          search: txnSearch || undefined,
          type: txnType,
          from: txnFrom || undefined,
          to: txnTo || undefined,
        },
      }),
    placeholderData: (prev) => prev,
  });

  const destFn = useServerFn(getRefundDestinationReport);
  const destinations = useQuery({
    queryKey: ["admin", "refund-destinations", from, to],
    queryFn: () => destFn({ data: { from, to } }),
    placeholderData: (prev) => prev,
  });

  const data = report.data;


  const exportRows = useMemo(() => {
    if (!data) return [];
    const rows: (string | number)[][] = [
      ["Wallet Report", `${from} to ${to}`],
      [],
      ["Metric", "Value"],
      ["Wallet liability (INR)", (data.liability_paise / 100).toFixed(2)],
      ["Wallet count", data.wallet_count],
      ["Average balance (INR)", (data.avg_balance_paise / 100).toFixed(2)],
      ["Total credits (INR)", (data.total_credits_paise / 100).toFixed(2)],
      ["Total debits (INR)", (data.total_debits_paise / 100).toFixed(2)],
      ["Refunds to wallet (INR)", (data.refunds_paise / 100).toFixed(2)],
      ["Promotional credits (INR)", (data.promotional_paise / 100).toFixed(2)],
      ["Wallet payments (INR)", (data.wallet_payments_paise / 100).toFixed(2)],
      ["Transaction count", data.txn_count],
      [],
      ["Day", "Credits (INR)", "Debits (INR)"],
      ...data.daily.map((d) => [
        d.day,
        (d.credit_paise / 100).toFixed(2),
        (d.debit_paise / 100).toFixed(2),
      ]),
      [],
      ["Top wallet user", "Balance (INR)", "Lifetime credit (INR)"],
      ...data.top_users.map((u) => [
        u.customer_name ?? u.customer_id,
        (u.balance_paise / 100).toFixed(2),
        (u.lifetime_credit_paise / 100).toFixed(2),
      ]),
      [],
      ["Largest refund", "Order ref", "Amount (INR)", "Date"],
      ...data.largest_refunds.map((r) => [
        r.customer_name ?? r.customer_id,
        r.order_ref ?? "",
        (r.amount_paise / 100).toFixed(2),
        r.created_at,
      ]),
    ];
    return rows;
  }, [data, from, to]);

  return (
    <div>
      <AdminPageHeader
        title="Wallet"
        description="Wallet liability, credits, debits, refunds and transaction history."
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={!data}
              onClick={() => downloadCsv(`wallet-report-${from}-to-${to}.csv`, toCsv(exportRows))}
            >
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!data}
              onClick={() => downloadXlsx(`wallet-report-${from}-to-${to}.xlsx`, exportRows)}
            >
              Export Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              Print
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div>
          <Label htmlFor="wallet-from" className="text-xs">From</Label>
          <Input
            id="wallet-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="wallet-to" className="text-xs">To</Label>
          <Input
            id="wallet-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>

      {report.isError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Couldn't load the wallet report.</AlertDescription>
        </Alert>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <StatCard
          label="Current Wallet Liability"
          value={data ? formatPaise(data.liability_paise) : "—"}
          hint={data ? `${data.wallet_count} wallets` : undefined}
          loading={report.isLoading}
        />
        <StatCard
          label="Total Credits"
          value={data ? formatPaise(data.total_credits_paise) : "—"}
          loading={report.isLoading}
        />
        <StatCard
          label="Total Debits"
          value={data ? formatPaise(data.total_debits_paise) : "—"}
          loading={report.isLoading}
        />
        <StatCard
          label="Refunds to Wallet"
          value={data ? formatPaise(data.refunds_paise) : "—"}
          loading={report.isLoading}
        />
        <StatCard
          label="Promotional Credits"
          value={data ? formatPaise(data.promotional_paise) : "—"}
          loading={report.isLoading}
        />
        <StatCard
          label="Average Wallet Balance"
          value={data ? formatPaise(data.avg_balance_paise) : "—"}
          loading={report.isLoading}
        />
        <StatCard
          label="Wallet Payments"
          value={data ? formatPaise(data.wallet_payments_paise) : "—"}
          loading={report.isLoading}
        />
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Daily credits / debits</CardTitle>
          </CardHeader>
          <CardContent>
            {report.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : data && data.daily.length > 0 ? (
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Day</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead className="text-right">Debits</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.daily.map((d) => (
                      <TableRow key={d.day}>
                        <TableCell className="text-sm">{d.day}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatPaise(d.credit_paise)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatPaise(d.debit_paise)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No data for this range.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Top wallet users</CardTitle>
          </CardHeader>
          <CardContent>
            {report.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : data && data.top_users.length > 0 ? (
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Lifetime credit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.top_users.map((u) => (
                      <TableRow key={u.customer_id}>
                        <TableCell className="text-sm">{u.customer_name ?? u.customer_id}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatPaise(u.balance_paise)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatPaise(u.lifetime_credit_paise)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No wallet users yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-sm">Largest refunds</CardTitle>
        </CardHeader>
        <CardContent>
          {report.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : data && data.largest_refunds.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.largest_refunds.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm">{r.customer_name ?? r.customer_id}</TableCell>
                      <TableCell className="font-mono text-xs">{r.order_ref ?? "—"}</TableCell>
                      <TableCell className="text-xs">{formatISTDateTime(r.created_at)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatPaise(r.amount_paise)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No refunds in this range.</p>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-sm">Refund destinations</CardTitle>
        </CardHeader>
        <CardContent>
          {destinations.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : destinations.data && destinations.data.count > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Destination</TableHead>
                  <TableHead className="text-right">Refunds</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {destinations.data.by_method.map((m) => (
                  <TableRow key={m.method}>
                    <TableCell className="text-sm capitalize">{m.method}</TableCell>
                    <TableCell className="text-right tabular-nums">{m.count}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatPaise(m.amount_paise)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="text-sm font-semibold">Total</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {destinations.data.count}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {formatPaise(destinations.data.total_paise)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No refunds in this range.</p>
          )}
        </CardContent>
      </Card>



      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-sm">Transactions</CardTitle>
          <CardDescription>All wallet transactions across customers.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="mb-4 flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setTxnPage(1);
              setTxnSearch(txnSearchInput.trim());
            }}
          >
            <div>
              <Label className="text-xs">Search</Label>
              <Input
                value={txnSearchInput}
                onChange={(e) => setTxnSearchInput(e.target.value)}
                placeholder="Customer, reference, order"
                className="w-56"
              />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select
                value={txnType}
                onValueChange={(v) => {
                  setTxnType(v);
                  setTxnPage(1);
                }}
              >
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {WALLET_TXN_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t.replace(/_/g, " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={txnFrom} onChange={(e) => { setTxnFrom(e.target.value); setTxnPage(1); }} />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={txnTo} onChange={(e) => { setTxnTo(e.target.value); setTxnPage(1); }} />
            </div>
            <Button type="submit">Search</Button>
          </form>

          {txns.isError ? (
            <Alert variant="destructive">
              <AlertDescription>Couldn't load transactions.</AlertDescription>
            </Alert>
          ) : txns.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : txns.data && txns.data.rows.length > 0 ? (
            <>
              <div className="overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Balance after</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {txns.data.rows.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="text-xs">{formatISTDateTime(t.created_at)}</TableCell>
                        <TableCell className="text-sm">{t.customer_name ?? t.customer_id}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant={t.direction === "credit" ? "default" : "outline"}>
                            {t.txn_type.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{t.order_ref ?? "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {t.direction === "debit" ? "-" : "+"}
                          {formatPaise(t.amount_paise)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatPaise(t.balance_after_paise)}
                        </TableCell>
                        <TableCell className="text-xs">{t.status ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-3">
                <Pager page={txnPage} pageSize={PAGE_SIZE} total={txns.data.total} onPageChange={setTxnPage} />
              </div>
            </>
          ) : (
            <p className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No transactions match this filter.
            </p>
          )}
        </CardContent>
      </Card>

      <WalletSettingsSection />
    </div>
  );
}

function WalletSettingsSection() {
  const queryClient = useQueryClient();
  const getFn = useServerFn(getAdminWalletSettings);
  const updateFn = useServerFn(updateAdminWalletSettings);

  const settings = useQuery({
    queryKey: ["admin", "wallet-settings"],
    queryFn: () => getFn(),
  });

  const [form, setForm] = useState<AdminWalletSettings | null>(null);

  const current = form ?? settings.data ?? null;

  const save = useMutation({
    mutationFn: (payload: AdminWalletSettings) => updateFn({ data: payload }),
    onSuccess: () => {
      toast.success("Wallet settings saved");
      queryClient.invalidateQueries({ queryKey: ["admin", "wallet-settings"] });
      setForm(null);
    },
    onError: () => toast.error("Couldn't save wallet settings"),
  });

  if (settings.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (settings.isError || !current) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Couldn't load wallet settings.</AlertDescription>
      </Alert>
    );
  }

  const update = <K extends keyof AdminWalletSettings>(key: K, value: AdminWalletSettings[K]) => {
    setForm({ ...current, [key]: value });
  };

  const dirty = form !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Wallet Settings</CardTitle>
        <CardDescription>Controls checkout redemption, refunds and notifications.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div>
            <Label>Wallet enabled</Label>
            <p className="text-xs text-muted-foreground">Turn the wallet feature on or off site-wide.</p>
          </div>
          <Switch checked={current.wallet_enabled} onCheckedChange={(v) => update("wallet_enabled", v)} />
        </div>
        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div>
            <Label>Checkout redemption enabled</Label>
            <p className="text-xs text-muted-foreground">Allow customers to pay with wallet balance at checkout.</p>
          </div>
          <Switch
            checked={current.checkout_redemption_enabled}
            onCheckedChange={(v) => update("checkout_redemption_enabled", v)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Max redemption percent</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={current.max_redemption_percent}
              onChange={(e) => update("max_redemption_percent", Number(e.target.value))}
            />
          </div>
          <div>
            <Label className="text-xs">Min online remainder (paise)</Label>
            <Input
              type="number"
              min={0}
              value={current.min_online_remainder_paise}
              onChange={(e) => update("min_online_remainder_paise", Number(e.target.value))}
            />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div>
            <Label>Auto-refund cancelled orders</Label>
            <p className="text-xs text-muted-foreground">Automatically refund cancelled orders to the wallet.</p>
          </div>
          <Switch
            checked={current.auto_refund_cancelled_orders}
            onCheckedChange={(v) => update("auto_refund_cancelled_orders", v)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <Label>Email notify</Label>
            <Switch checked={current.notify_email} onCheckedChange={(v) => update("notify_email", v)} />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <Label>WhatsApp notify</Label>
            <Switch checked={current.notify_whatsapp} onCheckedChange={(v) => update("notify_whatsapp", v)} />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <Label>SMS notify</Label>
            <Switch checked={current.notify_sms} onCheckedChange={(v) => update("notify_sms", v)} />
          </div>
        </div>
        <div>
          <Label className="text-xs">Wallet terms</Label>
          <Textarea
            rows={5}
            value={current.wallet_terms ?? ""}
            onChange={(e) => update("wallet_terms", e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button disabled={!dirty || save.isPending} onClick={() => form && save.mutate(form)}>
            Save
          </Button>
          <Button variant="outline" disabled={!dirty || save.isPending} onClick={() => setForm(null)}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
